// ===========================================
// SIDE PANEL - Sliding Panel with CHAT/BUDDY/STATS tabs
// ===========================================

import { useState, useRef, useEffect } from 'react';
import { StatsPanel, StatsData } from './StatsPanel';
import { useGameScale } from '../../hooks/useGameScale';
import { useCoachStore, type CoachMessage } from '../../stores/coachStore';
import { useAuthStore } from '../../stores/authStore';

// Sidebet image imports
import sidebet1 from '../../assets/cosmetics/sidebets/1.png';
import sidebet2 from '../../assets/cosmetics/sidebets/2.png';
import sidebet3 from '../../assets/cosmetics/sidebets/3.png';
import sidebet4 from '../../assets/cosmetics/sidebets/4.png';
import sidebet5 from '../../assets/cosmetics/sidebets/5.png';
import sidebet6 from '../../assets/cosmetics/sidebets/6.png';
import sidebet7 from '../../assets/cosmetics/sidebets/7.png';
import sidebet8 from '../../assets/cosmetics/sidebets/8.png';
import sidebet9 from '../../assets/cosmetics/sidebets/9.png';
import sidebet10 from '../../assets/cosmetics/sidebets/10.png';
import sidebet11 from '../../assets/cosmetics/sidebets/11.png';
import sidebet12 from '../../assets/cosmetics/sidebets/12.png';

// ===========================================
// TYPES
// ===========================================

interface ChatMessage {
  username: string;
  message: string;
  isSystem?: boolean;
}

type TabType = 'chat' | 'buddy' | 'stats' | 'sidebets';

// Sidebet type identifier (used by PixiJS modal)
export type SidebetId = 'perfect-pairs' | 'twenty-one-plus-three' | 'top-three' | 'lucky-ladies' | 'royal-match' | 'lucky-lucky' | 'blazing-sevens' | 'busted-bet' | 'over-under-13' | 'mega-hand' | 'straight' | 'flush';

// Sidebet configuration
interface SidebetConfig {
  id: SidebetId;
  title: string;
  image: string;
}

const SIDEBETS: SidebetConfig[] = [
  { id: 'perfect-pairs', title: 'Perfect Pairs', image: sidebet1 },
  { id: 'twenty-one-plus-three', title: 'Twenty-One +3', image: sidebet2 },
  { id: 'top-three', title: 'Top Three', image: sidebet3 },
  { id: 'lucky-ladies', title: 'Lucky Ladies', image: sidebet4 },
  { id: 'royal-match', title: 'Royal Match', image: sidebet5 },
  { id: 'lucky-lucky', title: 'Lucky Lucky', image: sidebet6 },
  { id: 'blazing-sevens', title: 'Blazing Sevens', image: sidebet7 },
  { id: 'busted-bet', title: 'BUSTED! BET', image: sidebet8 },
  { id: 'over-under-13', title: 'Over/Under 13', image: sidebet9 },
  { id: 'mega-hand', title: 'MEGA HAND', image: sidebet10 },
  { id: 'straight', title: 'Straight', image: sidebet11 },
  { id: 'flush', title: 'Flush', image: sidebet12 },
];

interface SidePanelProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  stats: StatsData | null;
  onRequestStats?: () => void;
  /** Which side of the screen to display the panel (default: 'left') */
  side?: 'left' | 'right';
  /** Canvas height in unscaled pixels - tabs will center at 50% of this height */
  canvasHeight?: number;
  /** Small viewport mode - uses absolute positioning, 2x width, 2x font sizes */
  smallViewportMode?: boolean;
  /** Header height to use for positioning (default: 56) */
  headerHeight?: number;
  /** Override scale value (uses useGameScale() if not provided) */
  scale?: number;
  /** Container height for small viewport mode (needed for proper height calculation) */
  containerHeight?: number;
  /** Callback when panel opens/closes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Callback when a sidebet is clicked (opens the sidebet modal) */
  onSidebetClick?: (sidebetId: SidebetId) => void;
  /** List of active sidebet IDs (to show green border indicator) */
  activeSidebets?: SidebetId[];
}

// ===========================================
// COMPONENT
// ===========================================

// Panel dimensions in unscaled pixels
const PANEL_WIDTH = 320;
const PANEL_WIDTH_SMALL_VIEWPORT = 480; // 1.5x width for small viewports
const DEFAULT_HEADER_HEIGHT = 56; // h-14 = 56px - matches GameTable header

// Font size options (12px / 16px / 20px) - normal mode
type FontSize = 'small' | 'medium' | 'large';
const FONT_SIZE_CLASSES: Record<FontSize, string> = {
  small: 'text-xs',     // 12px
  medium: 'text-base',  // 16px
  large: 'text-xl',     // 20px
};
// Font size options - small viewport mode (2x sizes: 24px / 32px / 40px)
const FONT_SIZE_CLASSES_SMALL_VIEWPORT: Record<FontSize, string> = {
  small: 'text-2xl',    // 24px (2x of 12px)
  medium: 'text-3xl',   // 30px (approx 2x of 16px)
  large: 'text-4xl',    // 36px (approx 2x of 20px)
};
const FONT_SIZE_STORAGE_KEY = 'sidePanelFontSize';

export function SidePanel({
  messages,
  onSendMessage,
  stats,
  onRequestStats,
  side = 'left',
  canvasHeight,
  smallViewportMode = false,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  scale: scaleProp,
  containerHeight,
  onOpenChange,
  onSidebetClick,
  activeSidebets = [],
}: SidePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [inputValue, setInputValue] = useState('');
  const [showLegendModal, setShowLegendModal] = useState(false);
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    const saved = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    return (saved as FontSize) || 'medium';
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const coachEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hookScale = useGameScale();
  // Use prop scale if provided, otherwise use hook scale
  // Ensure scale is never 0 or undefined to avoid division issues
  const scale = scaleProp ?? hookScale ?? 1;
  const safeScale = scale > 0 ? scale : 1;

  // Cycle through font sizes: small -> medium -> large -> small
  const cycleFontSize = () => {
    const sizes: FontSize[] = ['small', 'medium', 'large'];
    const currentIndex = sizes.indexOf(fontSize);
    const nextSize = sizes[(currentIndex + 1) % sizes.length];
    setFontSize(nextSize);
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, nextSize);
  };

  // Get current font class (2x sizes in small viewport mode)
  const fontSizeClasses = smallViewportMode ? FONT_SIZE_CLASSES_SMALL_VIEWPORT : FONT_SIZE_CLASSES;
  const fontClass = fontSizeClasses[fontSize];

  // Panel width (2x in small viewport mode)
  const panelWidth = smallViewportMode ? PANEL_WIDTH_SMALL_VIEWPORT : PANEL_WIDTH;

  // Coach state
  const coachMessages = useCoachStore((state) => state.messages);
  const showRecommendations = useAuthStore((state) => state.user?.showRecommendations ?? false);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen && activeTab === 'chat' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, activeTab]);

  // Auto-scroll buddy messages
  useEffect(() => {
    if (isOpen && activeTab === 'buddy' && coachEndRef.current) {
      coachEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [coachMessages, isOpen, activeTab]);

  // Focus input when chat panel opens
  useEffect(() => {
    if (isOpen && activeTab === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, activeTab]);

  // Request stats when stats tab opens
  useEffect(() => {
    if (isOpen && activeTab === 'stats' && onRequestStats) {
      onRequestStats();
    }
  }, [isOpen, activeTab, onRequestStats]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed) {
      onSendMessage(trimmed);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent game controls from triggering while typing
    e.stopPropagation();
  };

  const handleTabClick = (tab: TabType) => {
    if (isOpen && activeTab === tab) {
      // Clicking active tab closes panel
      setIsOpen(false);
      onOpenChange?.(false);
    } else {
      // Open panel and switch to tab
      setActiveTab(tab);
      setIsOpen(true);
      onOpenChange?.(true);
    }
  };

  // ===========================================
  // NEW STYLING SYSTEM (Feb 2026)
  // ===========================================
  // Mark (✓/✗/—) = Outcome (Was Buddy right?)
  //   - ✓ = good outcome (WIN, BLACKJACK)
  //   - ✗ = bad outcome (LOSS, BUST)
  //   - — = neutral outcome (PUSH)
  // Color = Adherence (Did player follow advice?)
  //   - GREEN = followed advice
  //   - RED = ignored advice
  // ===========================================

  // Background/border styling based on ADHERENCE (followed vs ignored)
  const getCoachMessageStyle = (msg: CoachMessage) => {
    // Pending (no outcome yet) - neutral white styling
    if (msg.outcome === null || msg.outcome === undefined) {
      return 'border-l-2 border-white/40 bg-white/10';
    }
    // Resolved - color based on adherence
    if (msg.ignored) {
      // Ignored advice - RED theme
      return 'border-l-2 border-red-400 bg-red-500/20';
    } else {
      // Followed advice - GREEN theme
      return 'border-l-2 border-green-400 bg-green-500/20';
    }
  };

  // OLD STYLING (commented out for reference)
  // const getCoachMessageStyle_OLD = (msg: CoachMessage) => {
  //   if (msg.ignored) {
  //     return 'border-l-2 border-gray-500 bg-gray-500/10 opacity-50';
  //   }
  //   switch (msg.outcome) {
  //     case 'good':
  //       return 'border-l-2 border-green-400 bg-green-500/20';
  //     case 'neutral':
  //       return 'border-l-2 border-yellow-400 bg-yellow-500/20';
  //     case 'bad':
  //       return 'border-l-2 border-pink-400 bg-pink-500/20';
  //     default:
  //       return 'border-l-2 border-white/40 bg-white/10';
  //   }
  // };

  // Text color based on ADHERENCE
  const getCoachMessageColor = (msg: CoachMessage) => {
    if (msg.outcome === null || msg.outcome === undefined) {
      return 'text-white/90'; // Pending
    }
    return msg.ignored ? 'text-red-300' : 'text-green-300';
  };

  // OLD TEXT COLOR (commented out for reference)
  // const getCoachMessageColor_OLD = (msg: CoachMessage) => {
  //   if (msg.ignored) {
  //     return 'text-gray-400';
  //   }
  //   switch (msg.outcome) {
  //     case 'good':
  //       return 'text-green-300';
  //     case 'neutral':
  //       return 'text-yellow-300';
  //     case 'bad':
  //       return 'text-pink-300';
  //     default:
  //       return 'text-white/90';
  //   }
  // };

  // Details text color based on ADHERENCE
  const getCoachDetailsColor = (msg: CoachMessage) => {
    if (msg.outcome === null || msg.outcome === undefined) {
      return 'text-white/50'; // Pending
    }
    return msg.ignored ? 'text-red-400/60' : 'text-green-400/60';
  };

  // OLD DETAILS COLOR (commented out for reference)
  // const getCoachDetailsColor_OLD = (msg: CoachMessage) => {
  //   if (msg.ignored) {
  //     return 'text-gray-500';
  //   }
  //   switch (msg.outcome) {
  //     case 'good':
  //       return 'text-green-400/60';
  //     case 'neutral':
  //       return 'text-yellow-400/60';
  //     case 'bad':
  //       return 'text-pink-400/60';
  //     default:
  //       return 'text-white/50';
  //   }
  // };

  // Mark color based on OUTCOME (intuitive colors for symbols)
  // ✓ = green (good), ✗ = pink (bad), — = yellow (neutral)
  // Background/border shows adherence (green=followed, red=ignored)
  const getMarkColor = (msg: CoachMessage) => {
    switch (msg.outcome) {
      case 'good':
        return 'text-green-400';
      case 'bad':
        return 'text-pink-500';
      case 'neutral':
        return 'text-yellow-400';
      default:
        return 'text-white/60';
    }
  };

  const getHeaderTitle = () => {
    switch (activeTab) {
      case 'chat': return 'TABLE CHAT';
      case 'buddy': return 'BUSTED! BUDDY';
      case 'stats': return 'STATISTICS';
      case 'sidebets': return 'SIDE BETS';
    }
  };

  // Compute unscaled height so that after scaling, it fills to bottom
  // After transform scale(s), rendered height = unscaledHeight * s
  // We want rendered height = availableHeight - headerHeight
  // So: unscaledHeight = (availableHeight - headerHeight) / s
  const availableHeight = smallViewportMode && containerHeight ? containerHeight : window.innerHeight;
  const panelUnscaledHeight = (availableHeight - headerHeight) / safeScale;

  // Positioning based on side prop
  const isLeftSide = side === 'left';

  // Position type: absolute for small viewport (inside rotated container), fixed otherwise
  const positionType = smallViewportMode ? 'absolute' : 'fixed';

  // Use the safe scale value for transforms
  const effectiveScale = safeScale;

  return (
    <>
      {/* Side Panel - Slides from left or right, scaled to match game table, below header */}
      <div
        className={`${positionType} z-40 transition-transform duration-300 ease-in-out ${isLeftSide ? 'left-0' : 'right-0'}`}
        style={{
          width: `${panelWidth}px`,
          top: `${headerHeight}px`,
          height: `${panelUnscaledHeight}px`,
          transform: `scale(${effectiveScale}) ${isOpen ? 'translateX(0)' : `translateX(${isLeftSide ? -panelWidth : panelWidth}px)`}`,
          transformOrigin: isLeftSide ? 'top left' : 'top right',
        }}
      >
        {/* Panel container */}
        <div className="h-full flex flex-col bg-black/40 backdrop-blur-sm">
          {/* Header with title and font size control */}
          <div
            className={`flex-shrink-0 bg-black/30 border-b border-white/10 flex items-center justify-between ${smallViewportMode ? 'px-6 py-4' : 'px-4 py-3'}`}
            style={{ minHeight: smallViewportMode ? '56px' : '40px' }}
          >
            <h3 className={`text-white/90 font-semibold tracking-wide ${smallViewportMode ? 'text-lg' : 'text-sm'}`}>
              {getHeaderTitle()}
            </h3>
            <button
              onClick={cycleFontSize}
              className={`text-white/60 hover:text-white/90 transition-colors rounded hover:bg-white/10 ${smallViewportMode ? 'px-3 py-2' : 'px-2 py-1'}`}
              title={`Font size: ${fontSize} (click to change)`}
            >
              <span className={`font-serif ${smallViewportMode ? 'text-lg' : 'text-sm'}`}>Aa</span>
              <span className={`ml-1 uppercase ${smallViewportMode ? 'text-sm' : 'text-[10px]'}`}>{fontSize[0]}</span>
            </button>
          </div>

          {/* Content area - CHAT */}
          {activeTab === 'chat' && (
            <>
              {/* Messages area */}
              <div className={`flex-1 overflow-y-auto px-3 py-2 space-y-2 ${fontClass}`}>
                {messages.length === 0 ? (
                  <div className="text-white/40 italic text-center mt-4">
                    No messages yet...
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`${msg.isSystem ? 'py-1 px-2 my-1 bg-yellow-500/20 rounded border-l-2 border-yellow-500' : ''}`}
                    >
                      {msg.isSystem ? (
                        <span className="text-yellow-300">{msg.message}</span>
                      ) : (
                        <>
                          <span className="text-yellow-400/90 font-medium">{msg.username}: </span>
                          <span className="text-white/80">{msg.message}</span>
                        </>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <form onSubmit={handleSubmit} className="flex-shrink-0 p-3 bg-black/30 border-t border-white/10 overflow-hidden">
                <div className="flex gap-2 items-center">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    maxLength={200}
                    className={`flex-1 min-w-0 px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/40 focus:outline-none focus:border-white/40 ${fontClass}`}
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim()}
                    className={`flex-shrink-0 flex items-center justify-center bg-green-600 hover:bg-green-500 disabled:bg-gray-600/50 disabled:cursor-not-allowed text-white font-medium rounded transition-colors ${smallViewportMode ? 'text-2xl w-16 h-12' : 'text-xs w-10 h-8'}`}
                  >
                    &gt;&gt;&gt;
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Content area - BUDDY */}
          {activeTab === 'buddy' && (
            <>
              {showRecommendations ? (
                // Enabled state - show recommendations
                <>
                  <div className={`flex-1 overflow-y-auto px-3 py-2 space-y-2 ${fontClass}`}>
                    {coachMessages.length === 0 ? (
                      <div className="text-white/40 italic text-center mt-4 space-y-2">
                        <div>No advice yet...</div>
                        <div className="opacity-70">Play a hand to see recommendations</div>
                      </div>
                    ) : (
                      coachMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`py-2 px-3 rounded flex items-start gap-2 ${getCoachMessageStyle(msg)}`}
                          title={msg.ignored ? 'Advice was not followed' : undefined}
                        >
                          {/* Message content */}
                          <div className="flex-1">
                            <div className={`font-medium whitespace-pre-line ${getCoachMessageColor(msg)}`}>
                              {msg.message}
                            </div>
                            {msg.details && (
                              <div className={`opacity-60 mt-1 ${getCoachDetailsColor(msg)}`}>
                                {msg.details}
                              </div>
                            )}
                          </div>
                          {/* Outcome indicator - Mark shows outcome, color shows adherence */}
                          {/* ✓ = Buddy was right, ✗ = Buddy was wrong, — = Neutral (PUSH) */}
                          {/* Green = followed advice, Red = ignored advice */}
                          {msg.outcome === 'good' && (
                            <span
                              className={`font-bold text-lg leading-none ${getMarkColor(msg)}`}
                              title={msg.ignored ? 'Buddy was right, but you ignored' : 'Buddy was right, you followed'}
                            >✓</span>
                          )}
                          {msg.outcome === 'bad' && (
                            <span
                              className={`font-bold text-lg leading-none ${getMarkColor(msg)}`}
                              title={msg.ignored ? 'Buddy was wrong, you ignored' : 'Buddy was wrong, you followed'}
                            >✗</span>
                          )}
                          {msg.outcome === 'neutral' && (
                            <span
                              className={`font-bold text-lg leading-none ${getMarkColor(msg)}`}
                              title={msg.ignored ? 'Push - you ignored advice' : 'Push - you followed advice'}
                            >—</span>
                          )}
                        </div>
                      ))
                    )}
                    <div ref={coachEndRef} />
                  </div>

                  {/* Buddy footer legend - clickable to open modal */}
                  <button
                    onClick={() => setShowLegendModal(true)}
                    className="w-full p-3 bg-black/30 border-t border-white/10 hover:bg-black/40 transition-colors"
                  >
                    <div className="text-xs text-white/40 text-center space-y-1">
                      {/* Mark = Outcome */}
                      <div className="space-x-3">
                        <span className="font-medium text-white/60">Mark:</span>
                        <span><span className="text-green-400 font-bold mr-1">✓</span>Right</span>
                        <span><span className="text-yellow-400 font-bold mr-1">—</span>Neutral</span>
                        <span><span className="text-pink-500 font-bold mr-1">✗</span>Wrong</span>
                      </div>
                      {/* Color = Adherence */}
                      <div className="space-x-3">
                        <span className="font-medium text-white/60">Color:</span>
                        <span><span className="text-white/50 font-bold mr-1">●</span>Pending</span>
                        <span><span className="text-green-400 font-bold mr-1">●</span>Followed</span>
                        <span><span className="text-red-400 font-bold mr-1">●</span>Ignored</span>
                      </div>
                      <div className="text-white/30 text-[10px] mt-1">Click for details</div>
                    </div>
                  </button>
                </>
              ) : (
                // Disabled state - show marketing content
                <div className={`flex-1 overflow-y-auto px-4 py-4 ${fontClass}`}>
                  <div className="space-y-4">
                    {/* Tagline */}
                    <div className="text-center">
                      <p className="text-green-400 font-medium">Your Personal Blackjack Advisor!</p>
                    </div>

                    {/* Description */}
                    <p className="text-white/80 leading-relaxed">
                      "BUSTED! Buddy" analyzes every dealt hand in real-time and recommends your next best action for optimal BlackJack gameplay. When it's your turn, your "BUSTED! Buddy" recommended action glows:
                    </p>

                    {/* Animated button example */}
                    <div className="flex flex-col items-center gap-1 py-3">
                      <button
                        className="px-8 py-3 bg-green-500 text-black font-bold rounded-lg text-lg neon-border"
                        style={{ '--neon-bg': '#22c55e' } as React.CSSProperties}
                        disabled
                      >
                        HIT
                      </button>
                      <span className="text-white/40 opacity-70">(example)</span>
                    </div>

                    {/* Features */}
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <span className="text-green-400">🎯</span>
                        <div>
                          <span className="text-white font-medium">Basic Strategy</span>
                          <p className="text-white/60 opacity-80 mt-0.5">The statistically optimal play for every situation, using an industry standard method perfected over decades of analysis.</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-green-400">📊</span>
                        <div>
                          <span className="text-white font-medium">True Count</span>
                          <p className="text-white/60 opacity-80 mt-0.5">Hi-Lo card counting adapted for multi-deck shoes, tracking the deck's favorability.</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-green-400">🔮</span>
                        <div>
                          <span className="text-white font-medium">BUSTED! Insights</span>
                          <p className="text-white/60 opacity-80 mt-0.5">Our proprietary blend of deviation indices and situational awareness that goes beyond textbook strategies.</p>
                        </div>
                      </div>
                    </div>

                    {/* Value prop */}
                    <p className="text-white/70 italic leading-relaxed">
                      Whether you're learning the fundamentals or refining advanced techniques, "BUSTED! Buddy" helps you internalize the patterns that separate casual players from seasoned card sharks!
                    </p>

                    {/* Pricing */}
                    <div className="bg-white/5 rounded-lg p-4 space-y-3">
                      <h4 className="text-white font-semibold text-center">Try BUSTED! Buddy</h4>
                      <p className="text-center text-white/70">
                        Enable BUSTED! Buddy today.
                      </p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg">
                          <span className="text-white/80">⚡ 24 hours</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">$1</span>
                            <button
                              className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white font-bold rounded transition-colors"
                              onClick={() => {
                                // TODO: Implement purchase flow for 24h
                                console.log('Buy 24h BUSTED! Buddy');
                              }}
                            >
                              BUY
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg">
                          <div>
                            <span className="text-white/80">💪 30 days</span>
                            <span className="text-green-400 opacity-80 ml-2">Save 83%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">$5</span>
                            <button
                              className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white font-bold rounded transition-colors"
                              onClick={() => {
                                // TODO: Implement purchase flow for 30d
                                console.log('Buy 30d BUSTED! Buddy');
                              }}
                            >
                              BUY
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-white/5 rounded-lg">
                          <div>
                            <span className="text-white/80">💎 1 year</span>
                            <span className="text-green-400 opacity-80 ml-2">Save 93%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">$25</span>
                            <button
                              className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white font-bold rounded transition-colors"
                              onClick={() => {
                                // TODO: Implement purchase flow for 1y
                                console.log('Buy 1y BUSTED! Buddy');
                              }}
                            >
                              BUY
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Content area - STATS */}
          {activeTab === 'stats' && (
            <StatsPanel stats={stats} fontClass={fontClass} />
          )}

          {/* Content area - SIDEBETS */}
          {activeTab === 'sidebets' && (
            <div className={`flex-1 overflow-y-auto ${fontClass}`}>
              <div className="space-y-1">
                {SIDEBETS.map((sidebet) => {
                  const isActive = activeSidebets.includes(sidebet.id);
                  return (
                    <button
                      key={sidebet.id}
                      onClick={() => onSidebetClick?.(sidebet.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 transition-colors text-left"
                    >
                      <div className="relative">
                        <img
                          src={sidebet.image}
                          alt={sidebet.title}
                          className={`object-contain ${smallViewportMode ? 'w-24 h-24' : 'w-20 h-20'} ${
                            isActive ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-transparent rounded-full' : ''
                          }`}
                        />
                        {isActive && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">✓</span>
                          </div>
                        )}
                      </div>
                      <span className={`font-medium ${isActive ? 'text-green-400' : 'text-white'}`}>{sidebet.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toggle Tabs - Stacked vertically on edge, scaled to match game table */}
      {/* If canvasHeight provided, center at 50% of canvas from header; otherwise 50% viewport */}
      <div
        className={`${positionType} z-50 flex flex-col gap-1 transition-all duration-300 ease-in-out`}
        style={{
          // Position at 50% of canvas height (from top of canvas, which is below header)
          top: canvasHeight
            ? `${headerHeight + (canvasHeight * effectiveScale / 2)}px`
            : '50%',
          // Position in viewport pixels: panel width * scale when open
          ...(isLeftSide
            ? { left: isOpen ? `${panelWidth * effectiveScale}px` : '0px' }
            : { right: isOpen ? `${panelWidth * effectiveScale}px` : '0px' }
          ),
          transform: `scale(${effectiveScale}) translateY(-50%)`,
          transformOrigin: isLeftSide ? 'top left' : 'top right',
        }}
      >
        {/* CHAT Tab */}
        <button
          onClick={() => handleTabClick('chat')}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
          className={`backdrop-blur-sm text-white font-medium tracking-wider transition-colors ${
            smallViewportMode ? 'px-3 py-6 text-base' : 'px-2 py-4 text-sm'
          } ${
            isLeftSide ? 'rounded-r-lg border border-l-0' : 'rounded-l-lg border border-r-0'
          } ${
            isOpen && activeTab === 'chat'
              ? 'bg-green-700/80 hover:bg-green-700 border-green-500/40'
              : 'bg-black/60 hover:bg-black/80 border-white/20'
          }`}
        >
          CHAT
        </button>

        {/* BUDDY Tab */}
        <button
          onClick={() => handleTabClick('buddy')}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
          className={`backdrop-blur-sm font-medium tracking-wider transition-colors ${
            smallViewportMode ? 'px-3 py-6 text-base' : 'px-2 py-4 text-sm'
          } ${
            isLeftSide ? 'rounded-r-lg border border-l-0' : 'rounded-l-lg border border-r-0'
          } ${
            isOpen && activeTab === 'buddy'
              ? 'bg-green-700/80 hover:bg-green-700 border-green-500/40 text-white'
              : 'bg-black/60 hover:bg-black/80 border-white/20 text-green-400'
          }`}
        >
          BUDDY
        </button>

        {/* STATS Tab */}
        <button
          onClick={() => handleTabClick('stats')}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
          className={`backdrop-blur-sm text-white font-medium tracking-wider transition-colors ${
            smallViewportMode ? 'px-3 py-6 text-base' : 'px-2 py-4 text-sm'
          } ${
            isLeftSide ? 'rounded-r-lg border border-l-0' : 'rounded-l-lg border border-r-0'
          } ${
            isOpen && activeTab === 'stats'
              ? 'bg-green-700/80 hover:bg-green-700 border-green-500/40'
              : 'bg-black/60 hover:bg-black/80 border-white/20'
          }`}
        >
          STATS
        </button>

        {/* BETS Tab */}
        <button
          onClick={() => handleTabClick('sidebets')}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
          className={`backdrop-blur-sm font-medium tracking-wider transition-colors ${
            smallViewportMode ? 'px-3 py-6 text-base' : 'px-2 py-4 text-sm'
          } ${
            isLeftSide ? 'rounded-r-lg border border-l-0' : 'rounded-l-lg border border-r-0'
          } ${
            isOpen && activeTab === 'sidebets'
              ? 'bg-purple-700/80 hover:bg-purple-700 border-purple-500/40 text-white'
              : 'bg-black/60 hover:bg-black/80 border-white/20 text-purple-400'
          }`}
        >
          BETS
        </button>
      </div>

      {/* Legend Modal */}
      {showLegendModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowLegendModal(false)}
        >
          <div
            className="bg-gray-900 border border-white/20 rounded-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-bold text-white">BUSTED! Buddy Legend</h2>
              <button
                onClick={() => setShowLegendModal(false)}
                className="text-white/60 hover:text-white text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-6">
              {/* Overview */}
              <div className="text-white/80 text-sm">
                Each recommendation in the Buddy chat shows two pieces of information:
                the <strong>mark</strong> (symbol) and the <strong>color</strong> (background).
              </div>

              {/* Mark Explanation */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
                  Mark = Was Buddy Right?
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-3 p-2 bg-white/5 rounded">
                    <span className="text-green-400 font-bold text-lg">✓</span>
                    <div>
                      <div className="text-white font-medium">Buddy Was Right</div>
                      <div className="text-white/60">The hand resulted in a WIN or BLACKJACK. Buddy's advice led to a favorable outcome.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2 bg-white/5 rounded">
                    <span className="text-yellow-400 font-bold text-lg">—</span>
                    <div>
                      <div className="text-white font-medium">Neutral / No Risk</div>
                      <div className="text-white/60">Either: (1) PUSH or SURRENDER outcome, (2) a "soft" HIT where bust wasn't possible - no real risk in the action.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2 bg-white/5 rounded">
                    <span className="text-pink-500 font-bold text-lg">✗</span>
                    <div>
                      <div className="text-white font-medium">Buddy Was Wrong</div>
                      <div className="text-white/60">The hand resulted in a LOSS or BUST. Despite the advice, the outcome was unfavorable.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Explanation */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
                  Color = Did You Follow Advice?
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-3 p-2 border-l-2 border-white/40 bg-white/10 rounded-r">
                    <span className="text-white/60 font-bold">●</span>
                    <div>
                      <div className="text-white font-medium">Grey = Pending</div>
                      <div className="text-white/60">The hand is still in progress. Outcome not yet determined.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2 border-l-2 border-green-400 bg-green-500/20 rounded-r">
                    <span className="text-green-400 font-bold">●</span>
                    <div>
                      <div className="text-white font-medium">Green = Followed Advice</div>
                      <div className="text-white/60">You took the action that Buddy recommended.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-2 border-l-2 border-red-400 bg-red-500/20 rounded-r">
                    <span className="text-red-400 font-bold">●</span>
                    <div>
                      <div className="text-white font-medium">Red = Ignored Advice</div>
                      <div className="text-white/60">You took a different action than what Buddy recommended.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Examples - sorted by Color (G,R) then Mark (C,X,D) */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
                  Examples
                </h3>
                <div className="space-y-2 text-sm">
                  {/* GC - Green Check */}
                  <div className="flex items-center gap-3 p-2 border-l-2 border-green-400 bg-green-500/20 rounded-r">
                    <span className="text-green-400 font-bold text-lg">✓</span>
                    <div className="text-white/80">
                      <strong>Green</strong> — Buddy said HIT, you HIT, and you WON. Perfect!
                    </div>
                  </div>
                  {/* GX - Green X */}
                  <div className="flex items-center gap-3 p-2 border-l-2 border-green-400 bg-green-500/20 rounded-r">
                    <span className="text-pink-500 font-bold text-lg">✗</span>
                    <div className="text-white/80">
                      <strong>Green</strong> — Buddy said STAND, you STOOD, but you LOST. Bad luck.
                    </div>
                  </div>
                  {/* GD - Green Dash */}
                  <div className="flex items-center gap-3 p-2 border-l-2 border-green-400 bg-green-500/20 rounded-r">
                    <span className="text-yellow-400 font-bold text-lg">—</span>
                    <div className="text-white/80">
                      <strong>Green</strong> — A soft HIT (no bust risk), or a PUSH/SURRENDER outcome. No definitive result to judge.
                    </div>
                  </div>
                  {/* RC - Red Check */}
                  <div className="flex items-center gap-3 p-2 border-l-2 border-red-400 bg-red-500/20 rounded-r">
                    <span className="text-green-400 font-bold text-lg">✓</span>
                    <div className="text-white/80">
                      <strong>Red</strong> — Buddy said HIT, you STOOD instead, but you WON anyway. Lucky!
                    </div>
                  </div>
                  {/* RX - Red X */}
                  <div className="flex items-center gap-3 p-2 border-l-2 border-red-400 bg-red-500/20 rounded-r">
                    <span className="text-pink-500 font-bold text-lg">✗</span>
                    <div className="text-white/80">
                      <strong>Red</strong> — Buddy said STAND, you HIT instead, and you BUSTED. Oops!
                    </div>
                  </div>
                  {/* RD - Red Dash */}
                  <div className="flex items-center gap-3 p-2 border-l-2 border-red-400 bg-red-500/20 rounded-r">
                    <span className="text-yellow-400 font-bold text-lg">—</span>
                    <div className="text-white/80">
                      <strong>Red</strong> — You ignored Buddy on a soft HIT or SURRENDER. Neutral outcome, but you deviated.
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="p-3 bg-white/5 rounded-lg text-sm text-white/70">
                <strong className="text-white">Quick Summary:</strong> The mark tells you if Buddy's prediction was right,
                and the color tells you if you trusted Buddy. Ideally, you want lots of <span className="text-green-400">green</span> backgrounds
                (following advice) with <span className="text-green-400">check</span> marks (good outcomes)!
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-white/10">
              <button
                onClick={() => setShowLegendModal(false)}
                className="w-full py-2 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SidePanel;
